<script setup>
import {Dialog, DialogPanel, Switch, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {useUserStore} from "@/Stores/userStore";
import {useStatusStore} from "@/Stores/statusStore";
import {useFeatureStore} from "@/Stores/featureStore";
import {useToast} from "vue-toastification";
import {useRouter} from "vue-router";
import {onMounted, reactive, ref, watch} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import featuresApi from "@/Api/features-api";

const router = useRouter();
const userStore = useUserStore()
const statusStore = useStatusStore()
const featureStore = useFeatureStore();
const toast = useToast();
const emits = defineEmits(['close']);

defineProps({
    show: Boolean,
});

const feedback = ref(null)
const score = ref(10) // Default to the highest score (10)
const data = reactive({
    request: {
        email_preferences: {
            energy_notifications: false,
            features_and_updates: false,
            community_content: false,
            promotions: false,
        },
    }
});

function submitNetPromoterScore() {
    const request = {
        score: score.value,
        feedback: feedback.value,
    }
    featuresApi.postNetPromoterScore(request).then(response => {
        featureStore.removeAlert('net-promoter')
        toast.success('Thank you for your feedback!')
    }).catch(function (error) {
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
    emits('close')
}

function setScore(value) {
    score.value = value // Sets the score to the value passed in
}

onMounted(() => {
    featureStore.removeAlert('email-preferences')
})

</script>

<template>
    <div>
        <TransitionRoot as="template" :show="show != null">
            <Dialog as="div" class="relative z-10" @close="$emit('close')">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="mt-0 sm:mt-8 fixed inset-0 z-10 overflow-y-auto">
                    <div class="flex min-h-full items-start justify-center text-center sm:items-center sm:p-4 p-0 ">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel>
                                <div class="grid-rows-2 w-full sm:w-[36rem]">
                                    <div class="p-4 sm:p-8 bg-gray-700 text-left text-gray-100 ">
                                        <div class="col-span-full">
                                            <div class="flex justify-start items-center mt-4">
                                                <FontAwesomeIcon size="2xl" :icon="['fad', 'comments']" />
                                                <p class="text-2xl ml-4 text-white">How likely are you to recommend us to a friend or colleague?</p>
                                            </div>
                                        </div>

                                        <div class="flex justify-between items-center  mt-6">
                                            <span class="text-white text-xs"></span>
                                            <span class="text-white text-xs">Very Likely</span>
                                        </div>

                                        <div class="grid grid-cols-10 gap-1">
                                            <!-- NPS Buttons -->
                                            <template v-for="n in 10">
                                                <button @click="setScore(n)" :class="n === score ? 'bg-blue-600' : 'bg-gray-600'" class="focus:outline-none auto text-white px-2 py-2 rounded-md hover:bg-blue-700 transition ease-in-out duration-200">
                                                    {{ n }}
                                                </button>
                                            </template>
                                        </div>

                                        <div class="mt-4">
                                            <label for="feedback" class="block text-white text-sm font-medium">Additional feedback:</label>
                                            <textarea id="feedback" v-model="feedback" rows="4" class="w-full px-3 py-2 text-gray-900 bg-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring focus:ring-blue-200" placeholder="Please provide any additional feedback here..."></textarea>
                                        </div>

                                        <div class="mt-6 flex justify-end">
                                            <button @click="submitNetPromoterScore()" class="bg-green-600 text-white px-6 py-1.5 rounded-md hover:bg-green-700 transition ease-in-out duration-200">
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<style scoped>
</style>
